import axios from 'axios';

import getAuthSession from './auth';

export interface AutofillUnansweredQuestion {
  shortCode: string;
  bestPractice: string;
}

export interface AutofillSkippedQuestion {
  shortCode: string;
  reason: string;
}

export interface AutofillResult {
  answeredCount: number;
  leftUnanswered: AutofillUnansweredQuestion[];
  skipped: AutofillSkippedQuestion[];
  scopeStable: boolean;
}

// Mirrors the API's refusal set: a complete or submitted survey has nothing to fill and
// cannot be corrected afterwards.
export const UNFILLABLE_SURVEY_STATUSES: string[] = ['complete', 'submittedForAssessment'];

export const AUTOFILL_MODES = {
  QUESTIONNAIRE: 'questionnaire',
  ASSESSOR: 'assessor',
} as const;

export type AutofillMode = (typeof AUTOFILL_MODES)[keyof typeof AUTOFILL_MODES];

const AUTOFILL_PATHS: Record<AutofillMode, string> = {
  [AUTOFILL_MODES.QUESTIONNAIRE]: 'autofill-questionnaire-answers',
  [AUTOFILL_MODES.ASSESSOR]: 'autofill-assessor-answers',
};

// Internal testing tool (TPE-3037). Flag-gated on the API as well as in the UI; the
// endpoint fills every in-scope question and reports back the one it left behind.
export async function autofillAnswers(
  mode: AutofillMode,
  workflowId: number,
  leaveUnanswered: number = 1,
): Promise<AutofillResult> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/${AUTOFILL_PATHS[mode]}/${workflowId}/`,
    { leaveUnanswered },
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );

  return response.data as AutofillResult;
}

export default autofillAnswers;
